import type { Detector, FrameAnalyzerOptions, FrameResult } from '../types';
import { estimateHeadPose } from './pose-estimator';

export class FrameAnalyzer {
  private rafId: number | null = null;

  private video: HTMLVideoElement | null = null;

  private isPaused = false;

  private lastCapture = 0;

  private isProcessing = false;

  private frameInterval: number;

  private detectors: Detector[];

  private onFrame: (result: FrameResult) => void;

  constructor(options: FrameAnalyzerOptions) {
    this.frameInterval = 1000 / options.fps;
    this.detectors = options.detectors;
    this.onFrame = options.onFrame;
  }

  start(stream: MediaStream): void {
    this.video = document.createElement('video');
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('autoplay', '');
    this.video.setAttribute('muted', '');
    this.video.style.display = 'none';
    document.body.appendChild(this.video);

    this.video.srcObject = stream;

    const readyHandler = () => {
      this.video?.removeEventListener('loadedmetadata', readyHandler);
      this.captureFrame();
    };

    this.video.addEventListener('loadedmetadata', readyHandler);
  }

  pause(): void {
    this.isPaused = true;
  }

  resume(): void {
    this.isPaused = false;
    this.lastCapture = 0;
  }

  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.video) {
      this.video.srcObject = null;
      if (this.video.parentElement) {
        this.video.parentElement.removeChild(this.video);
      }
      this.video = null;
    }

    this.detectors.forEach(detector => {
      detector.destroy();
    });
  }

  private captureFrame = (): void => {
    if (!this.video) {
      return;
    }

    const now = performance.now();

    if (
      now - this.lastCapture >= this.frameInterval &&
      !this.isPaused &&
      !this.isProcessing
    ) {
      this.lastCapture = now;
      this.processFrame(now);
    }

    this.rafId = requestAnimationFrame(this.captureFrame);
  };

  private async processFrame(timestamp: number): Promise<void> {
    if (!this.video) {
      return;
    }

    this.isProcessing = true;

    try {
      const results = await Promise.all(
        this.detectors.map(detector => detector.detect(this.video!)),
      );

      const frameResult: FrameResult = {
        timestamp,
        face: undefined,
        headPose: undefined,
        hands: undefined,
      };

      results.forEach(result => {
        if (result.face) {
          frameResult.face = result.face;
        }
        if (result.hands) {
          frameResult.hands = result.hands;
        }
      });

      if (frameResult.face?.detected && frameResult.face.landmarks.length > 0) {
        frameResult.headPose = estimateHeadPose(frameResult.face.landmarks);
      }

      this.onFrame(frameResult);
    } catch {
      // Empty catch block
    } finally {
      this.isProcessing = false;
    }
  }
}
